import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { GRID_CLASSNAMES } from './constants';

import { FinanceFormValues, useFinanceFormContext } from '../../../context';

export type TermStandardButtonValues = {
  term: number;
  label: string;
  key: string;
  id: string;
  onClick: () => void;
};

export interface TermsStandardHook {
  terms: TermStandardButtonValues[] | undefined;
  gridClassName: string;
}

export default function useTermsStandard(): TermsStandardHook {
  const { allRates } = useFinanceFormContext();
  const { setValue } = useFormContext<FinanceFormValues>();

  const [apr, aprType, duration] = useWatch<
    FinanceFormValues,
    ['apr', 'aprType', 'duration']
  >({
    name: ['apr', 'aprType', 'duration'],
  });

  const key = useMemo(() => {
    if (!apr || !aprType) return undefined;
    return `${apr}-${aprType}`;
  }, [apr, aprType]);

  const terms = useMemo(() => {
    if (!key) return undefined;
    const aprRates = allRates?.[key];
    if (!aprRates) return undefined;
    return Object.keys(aprRates).map((currentTerm) => ({
      term: parseInt(currentTerm, 10),
      label: `${currentTerm} months`,
      key: `${apr}-${currentTerm}-months`,
      id: `${apr}-${currentTerm}-months`,
      onClick: () => setValue('duration', parseInt(currentTerm, 10)),
    }));
  }, [allRates, key, apr, setValue]);

  const gridBaseClassName = useMemo(() => {
    if (!terms) return GRID_CLASSNAMES[1];
    if (terms.length > 3) return GRID_CLASSNAMES[4];
    return GRID_CLASSNAMES[terms.length];
  }, [terms]);

  const gridClassName = useMemo(
    () =>
      ['gap-x-2 md:gap-x-3 gap-y-2 md:gap-y-3', gridBaseClassName].join(' '),
    [gridBaseClassName],
  );

  const aprRef = useRef<string | undefined>();
  useEffect(() => {
    if (!terms) return;
    if (aprRef.current === apr) return;
    aprRef.current = apr;
    setValue('duration', terms[0].term);
  }, [apr, duration, terms, setValue]);

  return {
    terms,
    gridClassName,
  };
}
